import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function EnfieldCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Enfield Pothole Claims | RED Best Practice Rating | Fixtyer"
        description="Enfield Council earns GREEN on spend but RED on DfT best practice — with preventative maintenance projected at just 25% in 2025/26, carriageway resurfacing down to 3.77km, and 12% of 464km U-roads in RED condition. See what the AMBER rating means for your claim."
        canonical="/councils/enfield"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Enfield', url: '/councils/enfield' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Enfield outspends its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Enfield\'s projected 2025/26 capital spend of £3.0m exceeds its £1.007m DfT allocation. But your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spending. Enfield\'s overall rating is AMBER because condition is AMBER and best practice is RED: the council\'s own report projects just 25% preventative maintenance in 2025/26, down from 72% in 2021/22.',
          },
          {
            question: 'What does Enfield\'s RED best practice rating mean for my claim?',
            answer: 'The DfT best practice scorecard measures preventative maintenance, treatment of deteriorating roads, and adoption of efficient repair methods. Enfield\'s own transparency report shows preventative spend falling from 72% in 2021/22 to a projected 25% in 2025/26, carriageway resurfacing dropping from 12.5km to 3.77km per year, and the council admitting that maintenance of B, C and unclassified roads has declined. That is an official admission of a predominantly reactive programme — which strengthens arguments that defects can form and worsen between inspections.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 464km — 79.5% of Enfield\'s 584km network. At the latest AI survey, 12% of U-roads were in RED condition — roughly 56km of residential streets, closes and cul-de-sacs. The council admits levels of maintenance to the B, C and unclassified network have declined as it redirected capital to A-roads. If your defect was on a U-road, that admission and the 12–15% RED range over five years are directly relevant to Section 58.',
          },
          {
            question: 'Enfield surveys the whole network yearly with AI — does that weaken my claim?',
            answer: 'Not automatically. Enfield is an early adopter of yearly AI road surveying across its entire network, including unclassified roads — which is stronger than many London boroughs. But survey data is only useful if it drives timely treatment. The council\'s own figures show U-road RED condition at 12–15% for five years, preventative maintenance collapsing to 25%, and just 1.1km of carriageway resurfacing planned for 2025/26. Knowing a road is deteriorating without preventing defects is not the same as a reasonable maintenance system for the pothole that damaged your vehicle.',
          },
          {
            question: 'Does the shift from 72% to 25% preventative maintenance matter for Section 58?',
            answer: 'Yes. Enfield\'s own report states its 2025/26 programme is split 25% preventative to 75% reactive works, with all revenue spend assumed reactive. The council also notes that funding for reactive maintenance is allocated first, with remaining funds then going to preventative works. A council that formally prioritises patching over prevention cannot easily argue it took reasonable steps to stop defects forming — especially where prior reports or photos show a pothole worsening over time.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Enfield Council"
        councilSlug="enfield"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/enfield"
        headline="Enfield Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Enfield</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Enfield: Preventative Spend Collapsed From 72% to 25%
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Enfield Council projects £3.0m capital spend against a £1.007m DfT allocation and earns
            a GREEN spend scorecard. Yet the DfT rates its best practice{' '}
            <strong className="text-rose-400">RED</strong> — because Enfield&apos;s own report shows
            preventative maintenance falling from <strong className="text-rose-400">72% in 2021/22</strong> to
            a projected <strong className="text-rose-400">25% in 2025/26</strong>, carriageway resurfacing
            down to <strong className="text-amber-400">3.77km per year</strong>, and the council admitting
            that maintenance of B, C and unclassified roads{' '}
            <strong className="text-rose-400">has declined</strong>.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">75%</div>
            <div className="text-white font-medium mt-2">Reactive maintenance in 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              Enfield&apos;s own projected split — 25% preventative, 75% reactive. The DfT best
              practice scorecard is RED for a reason: this is a patch-first programme on London&apos;s
              7th-largest road network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* RED Best Practice */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why Best Practice Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            The DfT&apos;s best practice scorecard is Enfield&apos;s weakest rating — and the council&apos;s
            own spending table explains why
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Enfield's 25/26 Highway Maintenance programme is split 25% preventative works to 75% reactive works."
              source="Enfield Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£7.18m</td>
                  <td className="px-4 py-3 text-center">£609k</td>
                  <td className="px-4 py-3 text-center">69%</td>
                  <td className="px-4 py-3 text-center">31%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£8.60m</td>
                  <td className="px-4 py-3 text-center">£616k</td>
                  <td className="px-4 py-3 text-center text-emerald-600 font-medium">72%</td>
                  <td className="px-4 py-3 text-center">28%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£8.71m</td>
                  <td className="px-4 py-3 text-center">£617k</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£310k</td>
                  <td className="px-4 py-3 text-center">£4.33m</td>
                  <td className="px-4 py-3 text-center">£659k</td>
                  <td className="px-4 py-3 text-center">42%</td>
                  <td className="px-4 py-3 text-center">58%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£310k</td>
                  <td className="px-4 py-3 text-center">£3.69m</td>
                  <td className="px-4 py-3 text-center">£727k</td>
                  <td className="px-4 py-3 text-center">32%</td>
                  <td className="px-4 py-3 text-center">68%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£1.007m</td>
                  <td className="px-4 py-3 text-center">£3.0m</td>
                  <td className="px-4 py-3 text-center">£733k</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">25%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">75%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-8 italic">
            Preventative and reactive percentages are Enfield&apos;s own estimates. The council notes
            that for these calculations, all revenue spend is assumed reactive (road markings, defects,
            drainage, surveys, pumping stations, bridge repairs and street furniture).
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Resurfacing Collapse</h3>
              <p className="text-surface-600 text-sm mb-3">
                Planned carriageway resurfacing has fallen sharply — even as the council lists AI
                condition surveys as the basis for prioritisation:
              </p>
              <div className="bg-white rounded-lg border border-surface-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-100 border-b border-surface-200">
                      <th className="px-3 py-2 text-left font-semibold text-brand-900">Year</th>
                      <th className="px-3 py-2 text-center font-semibold text-brand-900">Resurfacing (km)</th>
                      <th className="px-3 py-2 text-center font-semibold text-brand-900">Surface treatment (km)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-surface-100">
                      <td className="px-3 py-2 font-mono">2020/21</td>
                      <td className="px-3 py-2 text-center">12.5</td>
                      <td className="px-3 py-2 text-center">0</td>
                    </tr>
                    <tr className="border-b border-surface-100">
                      <td className="px-3 py-2 font-mono">2021/22</td>
                      <td className="px-3 py-2 text-center">11.32</td>
                      <td className="px-3 py-2 text-center">0</td>
                    </tr>
                    <tr className="border-b border-surface-100">
                      <td className="px-3 py-2 font-mono">2022/23</td>
                      <td className="px-3 py-2 text-center">9.43</td>
                      <td className="px-3 py-2 text-center">4.27</td>
                    </tr>
                    <tr className="border-b border-surface-100">
                      <td className="px-3 py-2 font-mono">2023/24</td>
                      <td className="px-3 py-2 text-center">5.18</td>
                      <td className="px-3 py-2 text-center">0</td>
                    </tr>
                    <tr className="bg-rose-50">
                      <td className="px-3 py-2 font-mono font-bold">2024/25</td>
                      <td className="px-3 py-2 text-center font-bold text-rose-600">3.77</td>
                      <td className="px-3 py-2 text-center">1.72</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-surface-600 text-sm mt-3">
                Resurfacing has fallen 70% since 2020/21. For 2025/26, the council plans just 1.1km
                of carriageway resurfacing plus 2.24km of surface treatment on a 584km network —
                less than 0.6% of total road length.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Reactive First, Prevent Later</h3>
              <p className="text-rose-800 text-sm mb-3">
                Enfield&apos;s spending logic puts patching ahead of prevention:
              </p>
              <QuoteCard
                quote="To ensure this duty is fulfilled and the network is fit for purpose funding for this smaller capital/ reactive maintenance is allocated first. Remaining funds are then allocated to preventative maintenance which can be used to target those areas of the network which require increased reactive funding due to higher levels of deterioration."
                source="Enfield Council Local Highways Maintenance Transparency Report 2025"
              />
              <p className="text-rose-800 text-sm mt-3">
                A council that formally prioritises reactive repairs before preventative works is
                running the opposite of a &ldquo;predict and prevent&rdquo; programme — whatever its
                asset-management rhetoric says.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, RED Process</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£1.007m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.0m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">25%</div>
                <div className="text-brand-300 text-sm">Projected preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Enfield spends nearly <span className="text-emerald-400">triple its DfT allocation</span> — 
              and the DfT still rates its best practice <span className="text-rose-400">RED</span>.
              The money is there. The preventative programme is not.
            </p>
          </div>
        </div>
      </section>

      {/* Condition Data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Enfield&apos;s own transparency report — A-roads broadly stable,
            B and C roads slipping, U-roads persistently in double-digit RED
          </p>

          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-2">Network size (584km total)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">67km</div>
                <div className="text-sm text-surface-600">A roads (11.5%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">53km</div>
                <div className="text-sm text-surface-600">B and C roads (9.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">464km</div>
                <div className="text-sm text-surface-600">U roads (79.5%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">7th</div>
                <div className="text-sm text-surface-600">Largest network in London</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Enfield has the 4th largest principal road network in London. Most of the borough&apos;s
              roads — nearly four-fifths — are unclassified residential streets, closes and cul-de-sacs.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (67km): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center">82%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold">22%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road data is collected yearly. Enfield redirected its own capital to maintain these
              heaviest-trafficked routes — but the council admits that came at the cost of B, C and
              unclassified roads.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (53km): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">63%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold">65%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B and C roads rose from 6% to 8% since 2020. Good-condition roads fell
              from 71% to 65%, and amber roads climbed to 28–29%. Over a third of the B and C network
              now needs — or will soon need — maintenance.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-900 px-6 pt-6 mb-4">U-roads (464km): persistent RED</h3>
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
              </tbody>
            </table>
            <p className="text-surface-600 text-sm px-6 py-4">
              At 12% in 2024, roughly 56km of Enfield&apos;s residential network was in RED condition —
              roads that the council&apos;s own classification says &ldquo;should be considered for maintenance.&rdquo;
              The figure has sat between 10% and 15% for five straight years.
            </p>
          </div>
        </div>
      </section>

      {/* B/C/U Decline Admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The A-Road Priority Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Enfield&apos;s own explanation for why local roads lost out — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Prior to 2018/19 Enfield was in receipt of approximately £1m funding each year to support the maintenance of their A-Road network. Since then, this external funding has reduced significantly while deterioration of the network has continued. In order to address this deterioration Enfield has invested it's own capital funding in the maintenance of A-Roads the result of which is levels of maintenance to the B, C and unclassified network have declined."
              source="Enfield Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="In addition to funding variations the recent introduction of Low Traffic Neighbourhoods in Enfield effectively keeps traffic on the classified A, B & C network and has reduced the use of the Unclassified (local road) network. As more schemes are introduced to the Borough it remains to be seen how this will affect road conditions moving forward."
              source="Enfield Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Enfield formally acknowledges it redirected capital from B, C and unclassified roads
                to prop up A-roads after external funding dried up. That is documented knowledge that
                local road maintenance was deliberately deprioritised.
              </p>
              <p className="text-surface-600 text-sm">
                For claims on residential streets — 79.5% of the network — ask whether a council that
                admits declining U-road maintenance had a reasonable system in place for your specific defect.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The LTN Traffic Shift</h3>
              <p className="text-amber-800 text-sm mb-3">
                Enfield also notes that Low Traffic Neighbourhoods keep traffic on classified A, B
                and C roads while reducing use of unclassified routes — but says it remains to be seen
                how this will affect conditions.
              </p>
              <p className="text-amber-800 text-sm">
                If your pothole was on a classified road near an LTN boundary, increased traffic
                displacement is a relevant factor for what a reasonable inspection frequency looks like.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            13,051 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair on London&apos;s 7th-largest road network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,117</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,865</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,202</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,241</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,626</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13,051</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~7 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Enfield fills around seven potholes per day across its
                584km network. The council expects 2025/26 repairs to remain &ldquo;somewhere around
                2500-2800&rdquo; if winter weather is mild — confirming reactive repair as the default.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Defect Response Times</h3>
              <p className="text-amber-800 text-sm">
                Enfield repairs carriageway and footway defects on 2-hour/emergency, 24-hour, 7-day
                or 28-day schedules depending on severity. Section 41 of the Highways Act 1980 requires
                the network to be maintained in a condition safe for users — the question is whether
                your defect was caught within those windows, or left to worsen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection & AI */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            AI Surveys vs Reactive Reality
          </h2>
          <p className="text-surface-600 mb-8">
            Enfield invests in technology — but the maintenance programme does not always follow
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Enfield were one of the early adopters of AI road surveying technology. Investing in this has enabled us to monitor the entirety of our network on a yearly basis including unclassified roads. With full oversight of network conditions prioritisation of spend can be more objective."
              source="Enfield Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">What Enfield Does Well</h3>
              <ul className="text-emerald-800 text-sm space-y-2">
                <li>• Yearly AI survey of the entire 584km network, including all 464km of U-roads</li>
                <li>• Risk-based safety inspections from monthly to yearly depending on traffic and footfall</li>
                <li>• PAS2161-accredited survey supplier — ahead of the 2026/27 mandatory switch</li>
                <li>• Innovation partnerships: in-situ recycling, waste plastics in asphalt, mastic patching</li>
              </ul>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The Gap Between Data and Action</h3>
              <ul className="text-rose-800 text-sm space-y-2">
                <li>• U-road RED condition stuck at 10–15% for five years despite yearly AI surveys</li>
                <li>• Preventative spend collapsed from 72% to a projected 25%</li>
                <li>• Carriageway resurfacing down 70% — 12.5km to 3.77km per year</li>
                <li>• No AI equivalent for footways yet — visual inspections only</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, a council can defend a claim if it proves
              it took such care as was reasonable to see the road was not dangerous. For Enfield, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• If AI surveys show 12% of U-roads in RED every year, what was done about your road?</li>
              <li>• If preventative maintenance is just 25%, was your defect a predictable failure on a known-deteriorating route?</li>
              <li>• Were there prior reports (FixMyStreet, council defect reports) before your incident?</li>
              <li>• Does the council&apos;s admission of declining B, C and U-road maintenance cover your location?</li>
              <li>• For footway claims: the council admits there is no borough-wide AI footway survey yet</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim it did not know — then show it knew and chose reactive patching anyway.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend, RED-Practice Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Enfield is not Derbyshire — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projected capital nearly triple the £1.007m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Yearly AI survey of the entire network, including all unclassified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition broadly stable at 75% green in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented risk-based inspection regime and Well-managed Highway Infrastructure code compliance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Genuine innovation track record — recycling, waste plastics, PAS2161-accredited surveys
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing AI surveys and inspection records. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — DfT&apos;s official verdict on preventative maintenance and treatment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative spend collapsed from 72% to 25% in four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits B, C and U-road maintenance has declined
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12–15% of 464km U-roads in RED for five straight years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 13,051 pothole repairs in five years — reactive patching as default
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Just 1.1km resurfacing planned for 2025/26 on a 584km network
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and yearly AI surveys, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council defect reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the council&apos;s own admission of declining maintenance is your strongest structural argument</li>
              <li>• The gap between AI survey data and the 25% preventative programme — knowing vs doing</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Enfield&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Enfield?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A council with AI surveys and GREEN spend still demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED best practice argument</li>
                <li>• No B/C/U decline admission cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 75% reactive / RED best practice documented</li>
                <li>✅ B, C and U-road decline admission cited</li>
                <li>✅ 13,051 pothole repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Enfield</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=enfield"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Enfield Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Enfield outspends its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Enfield&apos;s projected 2025/26 capital
                spend of £3.0m exceeds its £1.007m DfT allocation. But your claim turns on whether the
                specific defect that damaged your vehicle was reasonably inspected and repaired under
                Section 58 — not on aggregate spending. Enfield&apos;s overall rating is AMBER because
                condition is AMBER and best practice is RED: the council&apos;s own report projects just
                25% preventative maintenance in 2025/26, down from 72% in 2021/22.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Enfield&apos;s RED best practice rating mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT best practice scorecard measures preventative maintenance, treatment of
                deteriorating roads, and adoption of efficient repair methods. Enfield&apos;s own
                transparency report shows preventative spend falling from 72% in 2021/22 to a projected
                25% in 2025/26, carriageway resurfacing dropping from 12.5km to 3.77km per year, and
                the council admitting that maintenance of B, C and unclassified roads has declined. That
                is an official admission of a predominantly reactive programme — which strengthens
                arguments that defects can form and worsen between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 464km — 79.5% of Enfield&apos;s 584km network. At the latest AI survey,
                12% of U-roads were in RED condition — roughly 56km of residential streets, closes
                and cul-de-sacs. The council admits levels of maintenance to the B, C and unclassified
                network have declined as it redirected capital to A-roads. If your defect was on a
                U-road, that admission and the 12–15% RED range over five years are directly relevant
                to Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Enfield surveys the whole network yearly with AI — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. Enfield is an early adopter of yearly AI road surveying across its
                entire network, including unclassified roads — which is stronger than many London
                boroughs. But survey data is only useful if it drives timely treatment. The council&apos;s
                own figures show U-road RED condition at 12–15% for five years, preventative maintenance
                collapsing to 25%, and just 1.1km of carriageway resurfacing planned for 2025/26.
                Knowing a road is deteriorating without preventing defects is not the same as a
                reasonable maintenance system for the pothole that damaged your vehicle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the shift from 72% to 25% preventative maintenance matter for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Enfield&apos;s own report states its 2025/26 programme is split 25% preventative
                to 75% reactive works, with all revenue spend assumed reactive. The council also notes
                that funding for reactive maintenance is allocated first, with remaining funds then
                going to preventative works. A council that formally prioritises patching over prevention
                cannot easily argue it took reasonable steps to stop defects forming — especially where
                prior reports or photos show a pothole worsening over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.enfield.gov.uk/__data/assets/pdf_file/0029/98705/Enfield-highway-maintenance-report-Roads-and-transport.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Enfield Council Local Highways Maintenance Transparency Report (25 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
